<template>
  <div class="event-messages" v-if="loaded">
    <div class="no-messages" v-if="eventMessages.length == 0">
      <h4>No messages yet</h4>
      <p class="large">
        Messages sent by the Tournament Director will be listed here. Players
        using Tjing for iOS or Android will recieve a push notification when a
        message is posted.
      </p>
    </div>
    <template v-for="message in eventMessages" v-else>
      <div class="event-message">
        <div class="message-header">
          <p>
            <b>{{ message.subject }}</b>
          </p>
          <div
            class="read-marker"
            v-if="
              isUserLoggedIn &&
              userRegisteredToThisEvent &&
              !message.userReadReceipt
            "
          ></div>
        </div>
        <div class="time-audience">
          <p class="since-posted">{{ timeSince(message.createdAt) }}</p>
          <template v-if="message.audience == 'WAITING'">
            <p class="round-separator"></p>
            <p class="target-audience">For waiting players</p>
          </template>
          <template v-if="message.audience == 'ACCEPTED'">
            <p class="round-separator"></p>
            <p class="target-audience">For confirmed players</p>
          </template>
        </div>
        <vue-markdown
          class="message-body"
          :source="message.message"
        ></vue-markdown>
        <div class="message-info" :class="{ 'extra-padding': !isUserLoggedIn }">
          <Comments />
          <p class="message-comments-count">
            {{ message.repliesCount }}
            {{ singularPlural('comment', message.repliesCount == 1) }}
          </p>
          <template v-if="isUserLoggedIn && userRegisteredToThisEvent">
            <p
              @click="unMarkAsRead(message)"
              v-if="message.userReadReceipt"
              class="mark-as-read"
            >
              Mark as unread
            </p>
            <p @click="markAsRead(message)" v-else class="mark-as-read">
              Mark as read
            </p>
          </template>
        </div>
        <div class="message-comments">
          <div class="comment" v-for="comment in message.replies">
            <UserProfileAvatar
              :profileImageUrl="comment.createdBy.profile.profileImageUrl"
              :backgroundColor="
                adminUserIds.includes(comment.createdBy.id)
                  ? 'ocean'
                  : 'twilight'
              "
              :initials="
                comment.createdBy.firstName.charAt(0) +
                comment.createdBy.lastName.charAt(0)
              "
            />
            <div class="comment-body">
              <p class="author">
                <b
                  >{{ comment.createdBy.firstName }}
                  {{ comment.createdBy.lastName }}</b
                >
                <span
                  class="admin-suffix"
                  v-if="adminUserIds.includes(comment.createdBy.id)"
                >
                  (admin)</span
                >
              </p>
              <p class="comment-message deleted" v-if="comment.isDeleted">
                This comment has been removed.
              </p>
              <p class="comment-message" v-else>{{ comment.message }}</p>
            </div>
            <div class="comment-footer">
              <div class="time-wrapper">
                <TjingToolTip
                  :text="formattedDate(comment.createdAt)"
                  :hideOnDesktop="false"
                  :bottomOffset="24"
                >
                  <p class="time">{{ timeSince(comment.createdAt) }}</p>
                </TjingToolTip>
                <template v-if="!comment.isDeleted">
                  <p class="round-separator"></p>
                  <p class="reply" @click="commentToReplyTo = comment">Reply</p>
                </template>
              </div>
              <template v-if="!comment.isDeleted">
                <div
                  class="show-more"
                  v-if="
                    comment.repliesCount > comment.replies.length &&
                    comment.showReplies === false
                  "
                  @click="loadReplies(comment)"
                >
                  <p>
                    <b>Show replies</b> ({{
                      comment.repliesCount - comment.replies.length
                    }})
                  </p>
                </div>
                <div
                  class="show-more"
                  v-else-if="
                    comment.repliesCount > 0 && comment.showReplies === false
                  "
                  @click="comment.showReplies = true"
                >
                  <p><b>Show replies</b> ({{ comment.repliesCount }})</p>
                </div>
                <div
                  class="show-more"
                  v-if="
                    comment.replies.length > 0 && comment.showReplies === true
                  "
                  @click="comment.showReplies = false"
                >
                  <p><b>Hide replies</b></p>
                </div>
              </template>
            </div>
            <div
              class="sub-replies"
              v-if="comment.showReplies && !comment.isDeleted"
            >
              <div class="sub-reply" v-for="reply in comment.replies">
                <UserProfileAvatar
                  :profileImageUrl="reply.createdBy.profile.profileImageUrl"
                  :backgroundColor="
                    adminUserIds.includes(reply.createdBy.id)
                      ? 'ocean'
                      : 'twilight'
                  "
                  :initials="
                    reply.createdBy.firstName.charAt(0) +
                    reply.createdBy.lastName.charAt(0)
                  "
                />
                <div class="sub-reply-body">
                  <p class="author">
                    <b
                      >{{ reply.createdBy.firstName }}
                      {{ reply.createdBy.lastName }}</b
                    >
                    <span
                      class="admin-suffix"
                      v-if="adminUserIds.includes(comment.createdBy.id)"
                    >
                      (admin)</span
                    >
                  </p>
                  <p class="message deleted" v-if="reply.isDeleted">
                    This reply has been removed.
                  </p>
                  <p class="message" v-else>{{ reply.message }}</p>
                </div>
                <div class="sub-reply-footer">
                  <div class="time-wrapper">
                    <p>{{ timeSince(reply.createdAt) }}</p>
                  </div>
                </div>
              </div>
            </div>
            <div
              class="show-more base-padding"
              v-if="
                comment.repliesCount > comment.replies.length &&
                comment.showReplies === true
              "
              @click="loadReplies(comment)"
            >
              <p>
                <b>Show more replies</b> ({{
                  comment.repliesCount - comment.replies.length
                }})
              </p>
            </div>
          </div>
        </div>
        <div
          class="load-more-comments"
          :class="{ space: !isUserLoggedIn }"
          v-if="message.replies.length < message.repliesCount"
          @click="loadComments(message)"
        >
          <p class="small"><b>Show more comments</b></p>
        </div>
        <div class="comment-section" v-if="isUserLoggedIn">
          <CommentField
            placeholder="Write a comment"
            :eventMessage="message"
            :messageReply="commentToReplyTo"
            :messageReplyId="commentToReplyTo ? commentToReplyTo.id : null"
            :avatarBackgroundColor="
              adminUserIds.includes(loggedInUserId) ? 'ocean' : 'twilight'
            "
            @cancelReply="commentToReplyTo = null"
            @sendMessage="createComment(message, $event)"
            @sendNestedMessage="
              createNestedReply(message.id, commentToReplyTo, $event)
            "
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import moment from 'moment'
import VueMarkdown from 'vue-markdown'
import Comments from '@/assets/icons/Comments'
import CommentField from '@/components/CommentField'
import UserProfileAvatar from '@/components/UIElements/UserProfileAvatar'
import dateFormats from '@/mixins/dateFormats.js'
import wordingUtils from '@/mixins/wordingUtils.js'
export default {
  components: { VueMarkdown, Comments, CommentField, UserProfileAvatar },
  mixins: [dateFormats, wordingUtils],
  name: 'PublicEventMessages',
  data() {
    return {
      eventMessages: null,
      commentToReplyTo: null,
      adminUserIds: [],
      loaded: false,
    }
  },
  computed: {
    userRegisteredToThisEvent() {
      for (var i = 0; i < this.$store.getters.registrations.length; i++) {
        if (
          this.$store.getters.registrations[i].event.id ==
            this.$router.currentRoute.params.eventId &&
          (this.$store.getters.registrations[i].status == 'ACCEPTED' ||
            this.$store.getters.registrations[i].status == 'PENDING')
        ) {
          return this.$store.getters.registrations[i]
        }
      }
      return null
    },
    isUserLoggedIn() {
      return this.$store.getters.isUserLoggedIn
    },
    loggedInUserId() {
      return this.$store.getters.userInfo.sessionInfo.userId
    },
  },
  methods: {
    async createNestedReply(eventMessageId, parentMessage, message) {
      try {
        const commentReplies = await this.$axios({
          headers: { Authorization: this.$store.state.user.sessionInfo.token },
          data: {
            query: `
              mutation CreateEventMessageReply($message: String!){
                CreateEventMessageReply(
                  eventMessageId:"${eventMessageId}",
                  parentMessageId: "${parentMessage.id}",
                  message: $message){
                  id
                }
              }
            `,
            variables: {
              message: message,
            },
          },
        })
        this.loadReplies(parentMessage)
      } catch (err) {
        this.$store.dispatch('showMessage', {
          show: true,
          message: 'Something went wrong',
          type: 'error',
        })
      }
    },
    async createComment(eventMessage, message) {
      try {
        const commentReplies = await this.$axios({
          headers: { Authorization: this.$store.state.user.sessionInfo.token },
          data: {
            query: `
              mutation CreateEventMessageReply($message: String!){
                CreateEventMessageReply(
                  eventMessageId:"${eventMessage.id}",
                  message: $message){
                  id
                }
              }
            `,
            variables: {
              message: message,
            },
          },
        })

        this.loadData()
      } catch (err) {
        this.$store.dispatch('showMessage', {
          show: true,
          message: 'Something went wrong',
          type: 'error',
        })
      }
    },
    async loadComments(message) {
      const skip = message.replies ? message.replies.length : 0
      const first = 10
      try {
        const comments = await this.$axios({
          headers: { Authorization: this.$store.state.user.sessionInfo.token },
          data: {
            query: `
              {
                eventMessageComments(eventMessageId:"${message.id}", first: ${first}, skip: ${skip}) {
                  id
                  eventMessageId
                  message
                  isDeleted
                  createdAt
                  createdBy {
                    id
                    firstName
                    lastName
                    profile{
                      profileImageUrl
                    }
                  }
                  updatedAt
                  updatedBy {
                    id
                  }
                  repliesCount
                  replies{
                    id
                    message
                    isDeleted
                    createdAt
                    updatedAt
                    createdBy{
                      id
                      firstName
                      lastName
                      profile{
                        profileImageUrl
                      }
                    }
                  }
                }
              }
            `,
          },
        })

        message.replies = [
          ...message.replies,
          ...comments.data.data.eventMessageComments,
        ]
      } catch (err) {
        this.$store.dispatch('showMessage', {
          show: true,
          message: 'Something went wrong',
          type: 'error',
        })
      }
    },
    async loadReplies(comment) {
      const skip = comment.replies ? comment.replies.length : 0
      const first = 10
      try {
        const commentReplies = await this.$axios({
          headers: { Authorization: this.$store.state.user.sessionInfo.token },
          data: {
            query: `
            {
              eventMessageCommentReplies(replyId:"${comment.id}",first:${first},skip:${skip}){
                id
                message
                isDeleted
                createdAt
                updatedAt
                createdBy{
                  id
                  firstName
                  lastName
                    profile{
                      profileImageUrl
                    }
                }
              }
            }
            `,
          },
        })
        comment.showReplies = true
        comment.replies = [
          ...comment.replies,
          ...commentReplies.data.data.eventMessageCommentReplies,
        ]
      } catch (err) {
        this.$store.dispatch('showMessage', {
          show: true,
          message: 'Something went wrong',
          type: 'error',
        })
      }
    },
    async loadData() {
      const eventMessages = await this.$axios({
        headers: { Authorization: this.$store.state.user.sessionInfo.token },
        data: {
          query: `
          {
            event(eventId: "${this.$router.currentRoute.params.eventId}") {
              tour{
                administrators{
                  userId
                }
              }
              eventMessages{
                id
                eventId
                subject
                message
                audience
                createdAt
                updatedAt
                userReadReceipt {
                  id
                  userId
                  messageId
                  readAt
                }
                repliesCount
                replies{
                  id
                  eventMessageId
                  message
                  isDeleted
                  createdAt
                  createdBy{
                    id
                    firstName
                    lastName
                    profile{
                      profileImageUrl
                    }
                  }
                  updatedAt
                  updatedBy{
                    id
                  }
                  repliesCount
                }
              }
            }
          }
          `,
        },
      })
      eventMessages.data.data.event.eventMessages.forEach((x) => {
        x.replies.forEach((y) => {
          y.replies = []
          y.showReplies = false
        })
      })
      this.eventMessages = eventMessages.data.data.event.eventMessages
      this.adminUserIds = [
        ...eventMessages.data.data.event.tour.administrators.map(
          (x) => x.userId
        ),
      ]

      this.loaded = true
    },
    formattedDate(date) {
      return moment(date).format('D MMM YYYY HH:mm')
    },
    async unMarkAsRead(message) {
      try {
        const readReceipt = await this.$axios({
          headers: { Authorization: this.$store.state.user.sessionInfo.token },
          data: {
            query: `
              mutation{
                DeleteEventMessageReadReceipt(messageId:"${message.id}")
              }
            `,
          },
        })

        message.userReadReceipt = null
        this.updateEventMessageMeta()
      } catch (err) {
        this.$store.dispatch('showMessage', {
          show: true,
          message: 'Something went wrong',
          type: 'error',
        })
      }
    },
    async updateEventMessageMeta() {
      const eventMessageMeta = await this.$axios({
        headers: { Authorization: this.$store.state.user.sessionInfo.token },
        data: {
          query: `
          {
            event(eventId: "${this.$router.currentRoute.params.eventId}") {
              eventMessageMeta {
                accepted
                pending
                public
                readByUser
                availableToUser
              }
            }
          }
          `,
        },
      })

      this.$store.dispatch(
        'setEventMessagesMeta',
        eventMessageMeta.data.data.event.eventMessageMeta
      )
    },
    async markAsRead(message) {
      try {
        let readReceipt = await this.$axios({
          headers: { Authorization: this.$store.state.user.sessionInfo.token },
          data: {
            query: `
              mutation{
                CreateEventMessageReadReceipt(messageId:"${message.id}"){
                  id
                }
              }
            `,
          },
        })

        message.userReadReceipt =
          readReceipt.data.data.CreateEventMessageReadReceipt

        this.updateEventMessageMeta()
      } catch (err) {
        this.$store.dispatch('showMessage', {
          show: true,
          message: 'Something went wrong',
          type: 'error',
        })
      }
    },
    dateFormatter(date) {
      return moment(date).format('D MMM HH:mm')
    },
  },
  beforeMount() {
    this.loadData()
  },
  beforeCreate() {},
  destroyed() {},
}
</script>

<style lang="scss">
.event-messages {
  .event-message {
    .message-body {
      padding: 12px 16px 0 16px;
      @import '@/styles/Typography';
      ul {
        list-style-type: disc;
        padding-inline-start: 40px;
        margin-bottom: 1rem;
      }

      p {
        overflow-wrap: anywhere;
      }
    }
  }
}

.event-message {
  p {
    margin: 0;
    line-height: normal;

    strong {
      @include Gilroy-Bold;
    }
  }
}
</style>
<style lang="scss" scoped>
@import '@/styles/Typography';

.message-comments-count {
  color: $strom;
}
.time-audience {
  display: flex;
  padding: 0 16px;
  margin-top: 6px;
  p {
    font-size: 0.75rem;
    color: $strom;
  }
}
.placeholder {
  color: $strom;
}
.admin-suffix {
  color: $strom;
}
.load-more-comments {
  padding-left: 15px;
  margin-top: 18px;
  cursor: pointer;

  &.space {
    margin-bottom: 18px;
  }
}
.sub-replies {
  width: 100%;
  margin-left: 42px;
}
.sub-reply {
  border-top: 1px solid $fog;
  padding-top: 18px;
  display: flex;
  flex-wrap: wrap;
  margin-top: 18px;

  .sub-reply-body {
    width: calc(100% - 42px);

    .author {
      font-size: 0.85rem;
    }

    .message {
      margin-top: 6px;

      &.deleted {
        color: $blizzard;
      }
    }
  }
  .sub-reply-footer {
    padding-left: 42px;
    margin-top: 8px;
  }
}
.comment {
  display: flex;
  margin: 18px 15px 18px 15px;
  padding: 18px 0px 0 0px;
  flex-wrap: wrap;
  border-top: 1px solid $fog;

  &:first-of-type {
    margin-top: 12px;
  }
  .comment-body {
    width: calc(100% - 42px);

    .author {
      font-size: 0.875rem;
    }
    .comment-message {
      margin-top: 6px;

      &.deleted {
        color: $blizzard;
      }
    }
  }

  .time-wrapper {
    display: flex;
    p {
      font-size: 0.75rem;
      color: $strom;
    }
  }
  .tooltip-content {
    bottom: 24px;
  }
  .comment-footer {
    width: 100%;
    margin-top: 8px;
    padding-left: 42px;
    display: flex;
    flex-wrap: wrap;

    .time {
      margin-right: 4px;
    }
    .reply {
      margin-left: 4px;
      cursor: pointer;
      color: $twilight;
    }

    .show-more {
      margin-top: 18px;
      width: 100%;
      cursor: pointer;
      p {
        font-size: 0.75rem;
        color: $midnight;
      }
    }
  }
}
.show-more {
  margin-top: 18px;
  width: 100%;
  cursor: pointer;
  p {
    font-size: 0.75rem;
    color: $midnight;
  }

  &.base-padding {
    padding-left: 42px;
  }
}
.message-info {
  margin-top: 8px;
  border-top: 1px solid $fog;
  margin: 20px 15px 0 15px;
  padding: 10px 0 0 0;
  display: flex;
  align-items: center;
  justify-content: flex-start;

  &.extra-padding {
    padding-bottom: 12px;
  }
  p {
    font-size: 0.75rem;
  }
  svg {
    margin-right: 8px;
    width: 14px;
    height: 9px;
  }
  .mark-as-read {
    margin-left: auto;
    color: $twilight;
    cursor: pointer;
  }
}
.user-profile-avatar,
.user-profile-image {
  margin-right: 10px;
}
.comment-section {
  padding: 14px 15px;
  background: $snow;
  border-top: 1px solid $sleet;
  margin-top: 18px;
  border-bottom-left-radius: 10px;
  border-bottom-right-radius: 10px;
}
.no-messages {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  margin-top: 50px;
  h4 {
    @include Gilroy-Bold;
    margin: 0 0 8px 0;
  }

  p {
    text-align: center;
    color: $strom;
    padding: 0 5%;
    line-height: 1.4em;
  }
}
h3 {
  margin-bottom: 24px;
}

.responsive-separator {
  display: none;
}

.audience {
  margin-left: auto;
  color: white;
  padding: 0px 10px;
  height: 24px;
  display: flex;
  align-items: center;
  background: $ocean;
  border-radius: 12px;
  text-transform: uppercase;
  @include Gilroy-Bold;
  letter-spacing: 1.2px;
  font-size: 12px;
  user-select: none;

  &.public {
    background: $cloud;
    color: $blizzard;
  }
  &.accepted {
    background: $sky;
  }
  &.pending {
    background: $sunset;
  }
}
.event-messages {
  padding: 0 12px;
}
.event-message {
  border: 1px solid $sleet;
  border-radius: 10px;
  &:not(:first-of-type) {
    margin-top: 32px;
  }

  .message-header {
    padding: 16px 18px 0 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .read-marker {
    background: $twilight;
    width: 12px;
    height: 12px;
    border-radius: 6px;
  }
}
@media (min-width: 480px) and (orientation: landscape) {
}

@media (min-width: 544px) {
}

@media (min-width: 768px) {
}

@media (min-width: 1024px) {
  .sub-reply {
    .sub-reply-body {
      .message {
        margin-top: 8px;
      }
    }
  }
  .time-audience {
    padding: 0 30px;
    margin-top: 8px;
    p {
      font-size: 0.85rem;
    }
  }
  .message-info {
    margin: 20px 30px 0 30px;
    &.extra-padding {
      padding-bottom: 18px;
    }
    p {
      font-size: 0.875rem;
    }
    svg {
      width: 19px;
      height: 12px;
      margin-right: 8px;
    }
  }
  .comment {
    margin: 12px 30px 18px 30px;
    .comment-body {
      .comment-message {
        margin-top: 8px;
      }
    }
  }
  .no-messages {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    h4 {
      margin: 0 0 24px 0;
    }

    p {
      color: $strom;
      padding: 0 20%;
      line-height: 1.4em;
    }
  }
  .event-message {
    .message-header {
      padding: 30px 30px 0 30px;
    }
    .message-body {
      padding: 12px 30px 0 30px;
      max-width: 70%;
    }

    .responsive-separator {
      display: block;
      margin: 0 8px;
    }
  }
}
</style>
